import React, { forwardRef, useImperativeHandle, useRef, useState } from 'react';

import { Popover as HeadlessPopover } from '@headlessui/react';
import cs from 'classnames';

import { usePopoverContext } from 'components/shared/Popovers/Popover/context/PopoverContext';
import Header from 'components/shared/shared/Header';

import PopoverButton from './components/Button/PopoverButton';
import PopoverPanel from './components/Panel/PopoverPanel';
import { useActiveOptionScroll } from './hooks/useActiveOptionScroll';
import { usePopoverHandlers } from './hooks/usePopoverHandlers';
import { usePopoverPosition } from './hooks/usePopoverPosition';

/**
 * @type {import('react').ForwardRefExoticComponent<import('./lib/propTypes').PopoverProps & import('react').RefAttributes<HTMLElement>>}
 */

const Popover = forwardRef((props, ref) => {
  const {
    position = 'right',
    isFixed = false,
    options = [],
    icon = 'context-menu-horizontal',
    iconSize = 16,
    iconColor,
    iconClassName = '',
    iconStroke = false,
    iconRight = '',
    iconRightColor,
    iconRightSize = 16,
    iconsShade = 500,
    iconRightClassName = '',
    optionIconSize = 16,
    optionIconColor = 'primary',
    optionArrowIcon = 'chevron-right-linear',
    optionArrowIconStroke = false,
    optionArrowColor = 'primary',
    optionActiveIcon = 'check-linear',
    optionsGroupBy,
    optionGroupLabelClassName = '',
    text = '',
    loading = false,
    disabled,
    disableBlur = false,
    className = '',
    buttonClassName = '',
    buttonTheme = null,
    buttonThemeType = 'secondary',
    textClassName = '',
    panelClassName = '',
    innerWrapperClassName = '',
    activeClassNames = {},
    isDropdown = false,
    isSeparator = false,
    isBorder = null,
    rerender = '',
    shouldCloseOnHover = true,
    dataQa = 'popover-button',
    id = null,
    onScroll = () => {},
    panelRef: outsidePanelRef = null,
    children,
    tooltip,
    labelTooltip,
    isTooltipDark = true,
    tooltipPlacement = 'bottom',
    customButton,
    customButtonTrigger,
    onOptionClick,
    ...headerProps
  } = props;

  // Refs and state
  const timeoutRef = useRef(null);
  const submenuRefs = useRef({});

  const popoverContext = usePopoverContext();
  const closeRef = useRef();
  const [isOpen, setIsOpen] = useState(false);
  const [hoveredIndexes, setHoveredIndexes] = useState({});

  // Custom hook for positioning
  const {
    panelFixedPosition,
    buttonRef,
    panelRef: positioningPanelRef,
    handlePanelRef,
    updatePopoverPosition
  } = usePopoverPosition({
    isFixed,
    position,
    rerender,
    isOpen,
    onClose: () => closeRef.current?.()
  });

  // Scroll to active option
  const { activeOptionRef } = useActiveOptionScroll({
    isOpen,
    panelRef: outsidePanelRef || positioningPanelRef
  });

  // Use outside panel ref if provided, otherwise use positioning hook's ref
  const panelRef = outsidePanelRef || positioningPanelRef;

  // Expose the buttonRef to the parent component
  useImperativeHandle(ref, () => ({ buttonRef }));

  // Event handlers
  const {
    handleMouseEnter,
    handleMouseLeave,
    handleOptionClick,
    handleButtonClick,
    onPopoverClose
  } = usePopoverHandlers({
    setIsOpen,
    setHoveredIndexes,
    timeoutRef,
    shouldCloseOnHover,
    popoverContext,
    onOptionClick,
    disableBlur,
    panelRef,
    updatePopoverPosition,
    submenuRefs
  });

  // Button props
  const buttonProps = {
    buttonRef,
    handleButtonClick,
    dataQa,
    buttonTheme,
    text,
    isSeparator,
    isBorder,
    buttonClassName,
    activeClassNames,
    disabled,
    buttonThemeType,
    loading,
    icon,
    iconSize,
    iconColor,
    iconsShade,
    iconStroke,
    iconClassName,
    textClassName,
    iconRight,
    iconRightSize,
    iconRightColor,
    iconRightClassName,
    isDropdown,
    tooltip,
    isTooltipDark,
    tooltipPlacement,
    customButton,
    customButtonTrigger,
    setIsOpen
  };

  // Panel props
  const panelProps = {
    isFixed,
    handlePanelRef,
    panelRef,
    onScroll,
    panelClassName,
    position,
    panelFixedPosition,
    options,
    optionsGroupBy,
    optionGroupLabelClassName,
    hoveredIndexes,
    handleMouseEnter,
    handleMouseLeave,
    handleOptionClick,
    activeOptionRef,
    submenuRefs,
    text,
    customButton,
    optionIconSize,
    optionIconColor,
    iconsShade,
    optionArrowIcon,
    optionArrowIconStroke,
    optionActiveIcon,
    optionArrowColor
  };

  return (
    <HeadlessPopover
      id={id}
      className={cs('w-fit', className)}
      onClose={onPopoverClose}
      onBlur={onPopoverClose}>
      {({ open, close }) => {
        closeRef.current = close;
        return (
          <>
            <Header {...headerProps} transcribing={false} tooltip={labelTooltip} />
            <div className={cs('relative', innerWrapperClassName)}>
              <PopoverButton {...buttonProps} open={open} />
              <PopoverPanel {...panelProps} close={close}>
                {children}
              </PopoverPanel>
            </div>
          </>
        );
      }}
    </HeadlessPopover>
  );
});

export default Popover;
